import React from 'react';
import { ApplicantFieldsBlock } from './ApplicantFieldsBlock';
import { FormSectionPassThroughProps } from './formSectionPassThroughTypes';
import { CoApplicantRow } from './coApplicantTypes';
import styles from '../../ManageDealDrawer.module.scss';

interface CoApplicantSectionProps extends FormSectionPassThroughProps {
    /** The `primaryApplicant` section config — its `config` (field map) is reused, unchanged, for every row. */
    primaryApplicantSection: any;
    coApplicants: CoApplicantRow[];
    onRemove: (id: string) => void;
    onFieldChange: (id: string, fieldKey: string, value: any, importanceKey?: string | null) => void;
}

// Seeds a row's formData from every field actually stored on the row (both the config-driven
// keys shared with `primaryApplicantSection.config` and the hand-rendered ones with no backend
// config entry - Phone/Email/permanentSameAsCurrent, see ApplicantFieldsBlock) so a saved value
// always prefills. Row-metadata keys are excluded - they're passed to ApplicantFieldsBlock as
// explicit props, not as form fields.
const ROW_METADATA_KEYS = ['id', 'persisted', 'directorId', 'managementProfileId', 'linkSource'];
const buildRowFormData = (row: CoApplicantRow): Record<string, any> => {
    const rowFormData: Record<string, any> = {};
    Object.keys(row).forEach((fieldKey) => {
        if (ROW_METADATA_KEYS.includes(fieldKey)) return;
        rowFormData[`${row.id}.${fieldKey}`] = row[fieldKey];
    });
    return rowFormData;
};

// Builds a row's change handler: strips the row-id prefix `onFieldChange` doesn't expect before
// delegating, for both the changed key and (when present) its importance-tracking key.
const makeRowHandleChange = (rowId: string, onFieldChange: CoApplicantSectionProps['onFieldChange']) => {
    const prefix = `${rowId}.`;
    const stripRowPrefix = (fullKey: string) => (fullKey.startsWith(prefix) ? fullKey.slice(prefix.length) : fullKey);
    return (key: string, value: any, importanceKey?: string | null) => {
        onFieldChange(rowId, stripRowPrefix(key), value, importanceKey ? stripRowPrefix(importanceKey) : importanceKey);
    };
};

// Renders every co-applicant card - the roster itself (add/remove existing directors, spawn a
// blank manual card) is driven by the "Select Applicants" picker + Add button at the top of the
// Applicant Details section (see ManageDealDrawer.tsx), not by this component.
export const CoApplicantSection: React.FC<CoApplicantSectionProps> = ({
    primaryApplicantSection,
    coApplicants,
    onRemove,
    onFieldChange,
    ...formSectionPassThroughProps
}) => {
    if (!primaryApplicantSection?.config || !coApplicants.length) return null;

    return (
        <>
            {coApplicants.map((row) => {
                const rowFormData = buildRowFormData(row);
                const rowHandleChange = makeRowHandleChange(row.id, onFieldChange);

                return (
                    <div className={styles.CoApplicantRow} key={row.id}>
                        <ApplicantFieldsBlock
                            sectionKey={row.id}
                            applicantConfig={primaryApplicantSection.config}
                            formData={rowFormData}
                            handleChange={rowHandleChange}
                            directorId={row.directorId}
                            managementProfileId={row.managementProfileId}
                            linkSource={row.linkSource}
                            onRemove={() => onRemove(row.id)}
                            {...formSectionPassThroughProps}
                        />
                    </div>
                );
            })}
        </>
    );
};

export default CoApplicantSection;
